import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AlertCircle, Info } from "lucide-react";
import type { SchemaField } from "../../components/SchemaEditor/SchemaEditor";
import { isWholeNumber, prettyJson } from "../../lib/schemaFields";
import styles from "./PlayerStatsEditor.module.css";

interface PlayerStatsEditorProps {
  /** Stats schema from games.config.profile.stats_schema. Empty → raw-only. */
  schema: SchemaField[];
  /**
   * The initial / canonical stats object. The component owns its editing
   * state after mount and emits every change via `onChange`; it does NOT
   * reconcile later `value` prop changes — it is uncontrolled, reset-by-key.
   * To reset the editor (discard, or a server refetch), the parent must
   * remount it by passing a changing React `key`. Do NOT feed the live
   * working copy back in as `value`.
   */
  value: Record<string, unknown>;
  /** Called with the next stats object whenever the editor changes. */
  onChange: (next: Record<string, unknown>) => void;
  /** Called with the editor's validity (false blocks the page Save). */
  onValidityChange: (valid: boolean) => void;
}

type Mode = "form" | "raw";

/**
 * Coerce a stat value from `value` to the string an int/string input shows.
 * Undefined/null → empty string.
 */
function toFieldString(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "boolean") return raw ? "true" : "false";
  return String(raw);
}

/**
 * Build a stats object from the per-field string state, coercing each value to
 * its schema type: `int` → number (or the raw string if not a whole number, so
 * the invalid edit round-trips), `bool` → boolean, `string` → string.
 */
function buildObject(
  schema: SchemaField[],
  fields: Record<string, string>,
): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const f of schema) {
    const s = fields[f.key] ?? "";
    if (f.type === "int") {
      obj[f.key] = isWholeNumber(s) ? Number(s.trim()) : s;
    } else if (f.type === "bool") {
      obj[f.key] = s === "true";
    } else {
      obj[f.key] = s;
    }
  }
  return obj;
}

/**
 * PlayerStatsEditor — schema-driven stats editor.
 * Ports docs/mockups/player-detail.html §1 (Form mode), §3 (Raw JSON mode),
 * §4 (validation-error field + empty-schema raw-only alert).
 */
export function PlayerStatsEditor({
  schema,
  value,
  onChange,
  onValidityChange,
}: PlayerStatsEditorProps) {
  const hasSchema = schema.length > 0;
  const idPrefix = useId();

  // Empty schema → no typed form is possible, raw-only.
  const [mode, setMode] = useState<Mode>(hasSchema ? "form" : "raw");

  // --- Form-mode state -----------------------------------------------------
  // Each field is kept as a string so we can show the raw, possibly-invalid
  // text the admin typed (e.g. "4.5" in an int field).
  //
  // Deliberate design: `value` only seeds these initializers once. The editor
  // then owns its state and is uncontrolled — the parent resets it by remount
  // (changing React `key`), never by feeding a new `value` prop. See the
  // `value` prop JSDoc above.
  const [fields, setFields] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of schema) init[f.key] = toFieldString(value[f.key]);
    return init;
  });

  // --- Raw-mode state ------------------------------------------------------
  const [rawText, setRawText] = useState<string>(() => prettyJson(value));

  // Keep `onValidityChange` referentially stable for the effect below: a parent
  // that rebuilds the callback every render would otherwise be told its own
  // news again on every render. Refreshed after the commit, never during
  // render — the reporting effect is declared later, so it sees this value.
  const onValidityRef = useRef(onValidityChange);
  useEffect(() => {
    onValidityRef.current = onValidityChange;
  }, [onValidityChange]);

  // Per-field error messages (form mode).
  const fieldErrors = useMemo(() => {
    const errs: Record<string, string | undefined> = {};
    for (const f of schema) {
      if (f.type === "int") {
        const s = fields[f.key] ?? "";
        if (!isWholeNumber(s)) errs[f.key] = "Must be a whole number.";
      }
    }
    return errs;
  }, [schema, fields]);

  const formValid = useMemo(
    () => Object.values(fieldErrors).every((e) => !e),
    [fieldErrors],
  );

  // Raw-mode parse: null error → valid object.
  const rawParse = useMemo(() => {
    try {
      const parsed = JSON.parse(rawText) as unknown;
      if (
        parsed === null ||
        typeof parsed !== "object" ||
        Array.isArray(parsed)
      ) {
        return { error: "Stats must be a JSON object." };
      }
      return { value: parsed as Record<string, unknown> };
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [rawText]);

  const valid = mode === "form" ? formValid : !rawParse.error;

  // Report validity to the parent on every change.
  useEffect(() => {
    onValidityRef.current(valid);
  }, [valid]);

  function emitForm(nextFields: Record<string, string>) {
    setFields(nextFields);
    // Emit from the freshly-computed fields, not the stale `fields` closure.
    onChange(buildObject(schema, nextFields));
  }

  function handleFieldChange(key: string, raw: string) {
    emitForm({ ...fields, [key]: raw });
  }

  function handleToggle(key: string, currentlyOn: boolean) {
    emitForm({ ...fields, [key]: currentlyOn ? "false" : "true" });
  }

  function handleRawChange(text: string) {
    setRawText(text);
    try {
      const parsed = JSON.parse(text) as unknown;
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        onChange(parsed as Record<string, unknown>);
      }
    } catch {
      // Invalid JSON — don't emit; validity effect surfaces the error.
    }
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    if (next === "raw") {
      // Seed the textarea from the current form object so the two stay synced.
      setRawText(prettyJson(buildObject(schema, fields)));
    } else {
      // Re-hydrate form fields from the raw object when it parses cleanly.
      // When the raw JSON is invalid we keep the form's pre-switch field
      // values: the invalid `rawText` is left untouched and reappears if the
      // user switches back to Raw, so nothing the admin typed is lost.
      if (!rawParse.error && rawParse.value) {
        const next2: Record<string, string> = {};
        for (const f of schema) {
          next2[f.key] = toFieldString(rawParse.value[f.key]);
        }
        setFields(next2);
      }
    }
    setMode(next);
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3 className={styles.cardTitle}>Stats</h3>
          <span className={styles.cardSub}>
            {hasSchema && mode === "form"
              ? `players.stats · ${schema.length} ${
                  schema.length === 1 ? "field" : "fields"
                } from schema`
              : "players.stats · raw JSONB"}
          </span>
        </div>
        {hasSchema && (
          <div className={styles.segmented} role="group" aria-label="Stats edit mode">
            <button
              type="button"
              className={`${styles.seg} ${mode === "form" ? styles.segActive : ""}`}
              aria-pressed={mode === "form"}
              onClick={() => switchMode("form")}
            >
              Form
            </button>
            <button
              type="button"
              className={`${styles.seg} ${mode === "raw" ? styles.segActive : ""}`}
              aria-pressed={mode === "raw"}
              onClick={() => switchMode("raw")}
            >
              Raw JSON
            </button>
          </div>
        )}
      </div>

      <div className={styles.cardBody}>
        {mode === "form" && hasSchema && (
          <div className={styles.statsGrid}>
            {schema.map((field) => {
              const fieldId = `${idPrefix}-${field.key}`;
              const errId = `${fieldId}-err`;
              const error = fieldErrors[field.key];
              const raw = fields[field.key] ?? "";

              if (field.type === "bool") {
                const on = raw === "true";
                return (
                  <div className={styles.statField} key={field.key}>
                    <label htmlFor={fieldId}>
                      <span className={styles.sk}>{field.key}</span>
                      <span className={styles.typeTag}>bool</span>
                    </label>
                    <div className={styles.boolRow}>
                      <button
                        type="button"
                        id={fieldId}
                        role="switch"
                        aria-checked={on}
                        aria-label={field.key}
                        className={`${styles.toggle} ${on ? styles.toggleOn : ""}`}
                        onClick={() => handleToggle(field.key, on)}
                      >
                        <span className={styles.knob} />
                      </button>
                      <span
                        className={`${styles.boolVal} ${
                          on ? styles.boolValOn : ""
                        }`}
                      >
                        {on ? "true" : "false"}
                      </span>
                    </div>
                  </div>
                );
              }

              const isInt = field.type === "int";
              return (
                <div className={styles.statField} key={field.key}>
                  <label htmlFor={fieldId}>
                    <span className={styles.sk}>{field.key}</span>
                    <span className={styles.typeTag}>{field.type}</span>
                  </label>
                  <input
                    id={fieldId}
                    type="text"
                    className={`${styles.input} ${isInt ? styles.mono : ""} ${
                      error ? styles.invalid : ""
                    }`}
                    value={raw}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errId : undefined}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  />
                  {error && (
                    <div className={styles.fieldError} id={errId}>
                      <AlertCircle size={12} aria-hidden className={styles.errIcon} />
                      {error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {mode === "raw" && (
          <>
            {hasSchema ? (
              <div className={`${styles.alert} ${styles.alertInfo}`}>
                <Info size={14} aria-hidden className={styles.alertIcon} />
                <div>
                  Raw mode edits the full <code className={styles.code}>stats</code>{" "}
                  object — including keys outside the schema. Saved values are
                  still validated against{" "}
                  <code className={styles.code}>stats_schema</code> on the server.
                </div>
              </div>
            ) : (
              <div className={`${styles.alert} ${styles.alertInfo}`}>
                <Info size={14} aria-hidden className={styles.alertIcon} />
                <div>
                  No <code className={styles.code}>stats_schema</code> defined for
                  this project — stats edit as raw JSON only. Add a schema in{" "}
                  <b>Config → Profile</b> to get a typed form.
                </div>
              </div>
            )}
            <textarea
              className={styles.jsonEditor}
              aria-label="Raw JSON"
              spellCheck={false}
              value={rawText}
              aria-invalid={rawParse.error ? true : undefined}
              onChange={(e) => handleRawChange(e.target.value)}
            />
            {rawParse.error ? (
              <div className={styles.fieldError}>
                <AlertCircle size={12} aria-hidden className={styles.errIcon} />
                {rawParse.error}
              </div>
            ) : (
              <div className={styles.helper}>
                Must be a valid JSON object. Invalid JSON blocks Save with an
                inline parser error.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
